import { useState } from 'react';
import type { TrialRecap } from '@/hooks/useTrialRecap';
import { PlanPickerCards } from '@/components/paywall/PlanPickerCards';
import type { BillingCycle } from '@/lib/pricing';
import { Check, Minus } from 'lucide-react';

interface Props {
  recap: TrialRecap | null;
  origin: 'trial-expired' | 'premium-action';
  lockedFeatureLabel?: string;
}

const LOSSES = [
  'Unlimited receipt scans (free stops at 5 per month)',
  'Every goal you started (free keeps 1)',
  'AI insights and natural-language entries',
  'Fennie evolution and health tracking',
  'Full budget history beyond 30 days',
];

export function PagePlanCompare({ recap, origin, lockedFeatureLabel }: Props) {
  const [cycle, setCycle] = useState<BillingCycle>('yearly');

  const headline =
    origin === 'trial-expired'
      ? 'My trial just ended.'
      : lockedFeatureLabel
      ? `${lockedFeatureLabel} needs Premium.`
      : 'This one needs Premium.';

  return (
    <div className="min-h-full px-4 py-8 max-w-3xl mx-auto">
      <p className="text-[10px] uppercase tracking-[0.3em] text-primary/70 mb-3 text-center">
        Chapter five
      </p>
      <h1 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight mb-3 text-center">
        {headline}
      </h1>
      <p className="text-sm text-muted-foreground/70 max-w-md mx-auto text-center mb-8">
        Here's what pauses on free - and what keeps working when I continue.
      </p>

      {/* Loss list */}
      <div className="rounded-2xl border border-border/30 bg-muted/5 p-5 mb-8 max-w-lg mx-auto">
        <p className="text-xs text-muted-foreground/70 uppercase tracking-widest mb-3">
          What stops without Premium
        </p>
        <ul className="space-y-2">
          {LOSSES.map((line) => (
            <li
              key={line}
              className="flex items-start gap-2 text-sm text-muted-foreground/80"
            >
              <Minus className="h-4 w-4 text-destructive/70 shrink-0 mt-0.5" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center justify-center gap-2 mb-4 text-xs text-muted-foreground/60">
        <Check className="h-3.5 w-3.5 text-primary" />
        <span>Everything above keeps running with Premium.</span>
      </div>

      <PlanPickerCards cycle={cycle} onCycleChange={setCycle} />

      <p className="text-center text-[10px] text-muted-foreground/40 mt-4">
        Cancel anytime. No hidden fees.
      </p>
    </div>
  );
}
